import { toCamelCase } from '@/shared/services/Convertor';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import type {
    CurrentUserType, InviteUserType, LoginType, RegisterType, ValidateInvitationTokenType,
} from '@/features/user/types/User';
import type { DeleteProps, GeneralResponseType, InsertProps, UpdateProps } from '@/shared/types/General';
import type { UserType } from '@/features/firm/types/Company';
import BaseRepository from '@/shared/repository/BaseRepository';
import type { BackgroundRequestConfig } from '@/shared/services/analytics/errorTelemetry';

export class UserRepository extends BaseRepository<any, any> {
    constructor() {
        super();

        this.endpoint = process.env.NEXT_PUBLIC_API_URL + '/v1';
        this.name = 'user';
        this.initialize();
    }

    public static create() : UserRepository {
        return new UserRepository();
    }

    public fetchCurrentUser = (config ?: object) : UseQueryResult<CurrentUserType> => {
        return useQuery(
            ['fetchCurrentUser'],
            async () : Promise<CurrentUserType> => {
                return toCamelCase<CurrentUserType>(
                    (await (await this.make()).get<any>(`${this.endpoint}/user`, config)).data,
                );
            },
        );
    };

    public logout = (config ?: object) : UseQueryResult<GeneralResponseType> => {
        return useQuery(
            ['logout'],
            async () : Promise<GeneralResponseType> => {
                try {
                    const response = await (await this.make()).post<any>(`${this.endpoint}/logout`, config);

                    return toCamelCase<GeneralResponseType>(response.data);
                } finally {
                    Cookies.remove('api_token');
                }
            },
        );
    };

    public validateInvitationToken = (token : string, config ?: object) : UseQueryResult<ValidateInvitationTokenType> => {
        return useQuery(
            ['fetchActivationToken', token],
            async () : Promise<ValidateInvitationTokenType> => {
                return toCamelCase<ValidateInvitationTokenType>(
                    (await (await this.make()).get<any>(`${this.endpoint}/users/validate-activation-token/${token}`, config)).data,
                );
            },
        );
    };

    public validateChangePasswordToken = (token : string, config ?: object) : UseQueryResult<ValidateInvitationTokenType> => {
        return useQuery(
            ['fetchActivationToken', token],
            async () : Promise<ValidateInvitationTokenType> => {
                return toCamelCase<ValidateInvitationTokenType>(
                    (await (await this.make()).get<any>(`${this.endpoint}/users/validate-change-password-token/${token}`, config)).data,
                );
            },
        );
    };

    public login = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType & { token ?: string }, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<LoginType>) => {
                const response = await (await this.make()).post<any>(`${this.endpoint}/login`, data, config);

                if (response.data.success && response.data.token) {
                    Cookies.set('api_token', response.data.token, { expires: 7, secure: true, sameSite: 'strict' });
                }

                return response.data;
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public forgotPassword = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<{ email : string }>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/forgot-password`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public requestMagicLink = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<{ email : string }>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/request-magic-link`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public verifyMagicLink = (token : string, config ?: object) : UseQueryResult<GeneralResponseType> => {
        return useQuery(
            ['verifyMagicLink', token],
            async () : Promise<GeneralResponseType> => {
                const data = toCamelCase<GeneralResponseType>(
                    (await (await this.make()).get<any>(`${this.endpoint}/users/verify-magic-link/${token}`, config)).data,
                );

                if (data.success && (data as any).token) {
                    Cookies.set('api_token', (data as any).token, { expires: 7, secure: true, sameSite: 'strict' });
                }

                return data;
            },
        );
    };

    public changePassword = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<{ email : string }>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/actions/change-password`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public register = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType & { token ?: string }, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<RegisterType>) => {
                const response = (await (await this.make()).post<any>(`${this.endpoint}/users/actions/register`, data, config)).data;

                if (response.success && response.token) {
                    Cookies.set('api_token', response.token, { expires: 7, secure: true, sameSite: 'strict' });
                }

                return response;
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public updateCurrentUser = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ id, data } : UpdateProps<UserType>) => {
                return (await (await this.make()).post<any>(`${this.endpoint}/users/actions/update_current_user?id=${id}`, data, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public inviteUser = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<InviteUserType>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/actions/invitation`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public resendInvitation = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<InviteUserType>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/actions/resend_invitation`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public removeInvitation = (config ?: object) : UseMutationResult<AxiosResponse<GeneralResponseType, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<InviteUserType>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/actions/remove_invitation`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public completeRegistration = (config ?: object) : UseMutationResult<GeneralResponseType, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<InviteUserType>) => {
                return ((await (await this.make()).post<any>(`${this.endpoint}/users/actions/complete_registration`, data, config)).data);
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public updateAddress = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ id, data } : UpdateProps<UserType>) => {
                return (await (await this.make()).put<any>(`${this.endpoint}/addresses/${id}`, data, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public createAddress = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<UserType>) => {
                return (await (await this.make()).post<any>(`${this.endpoint}/addresses`, data, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public changeAccount = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ data } : InsertProps<UserType>) => {
                return (await (await this.make()).post<any>(`${this.endpoint}/users/actions/change_account`, data, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public removeAddress = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ id } : DeleteProps) => {
                return (await (await this.make()).delete<any>(`${this.endpoint}/addresses/${id}`, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    public removeAddressMulti = (config ?: object) : UseMutationResult<AxiosResponse<any, any>, AxiosError<GeneralResponseType>, any, void> => {
        return useMutation({
            mutationFn: async ({ id } : DeleteProps) => {
                return (await (await this.make()).delete<any>(`${this.endpoint}/order-addresses?id=${id}`, config));
            },
            onMutate: () => {},
            onSuccess: () => {},
            onError: () => {},
            onSettled: () => {},
        });
    };

    /**
     * Heartbeat pro udržení session na legacy PHP
     */
    public heartbeat = async () : Promise<void> => {
        if (typeof document === 'undefined' || document.visibilityState !== 'visible' || navigator.onLine === false) return;

        try {
            const config : BackgroundRequestConfig = { backgroundOperation: 'session.heartbeat' };

            await (await this.make()).get(`${this.endpoint}/user`, config);

            // Sliding: úspěšný heartbeat prodlouží i platnost api_token cookie,
            // aby Bearer fallback nezmizel po fixních 7 dnech od loginu. Cookie
            // jen obnovujeme, pokud existuje (po logoutu ji neoživovat).
            const token = Cookies.get('api_token');

            if (token) {
                Cookies.set('api_token', token, { expires: 7, secure: true, sameSite: 'strict' });
            }
        } catch (error) {
            // Heartbeat failed
        }
    };
}

export default UserRepository;
